import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import type { GifPickerCard, GifPickerPill } from '@bonusly/bizy-kit';
import { Box, Button, Center, Flex, HStack, Image, Input, Link, Spinner, Text, useDisclosure, VStack } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

import { ChevronUpOutline } from '../../../design-system/icon';
import { useDebounceValue } from '../../../hooks/use-debounce-value';
import { GifProvider, PROVIDER_CONFIG } from '../../../lib/gif-provider';
import { Gif, SocialAttachment } from '../../action-input/lib/entities';
import { split } from '../../action-input/utils/split';

const MAX_RESULTS_HEIGHT = 320;

/**
 * Standalone (browse-only) render of a `gif_picker_card` node. The interactive
 * picker is embedded in the recognition draft card via {@link GifPicker}; this
 * only runs if a picker node ever renders without its sibling draft card.
 */
export function GifPickerCardView({ block }: { block: GifPickerCard; fontSize?: 'sm' | 'md'; isReadOnly?: boolean }) {
	return <GifPicker pills={block.payload.pills} primaryQuery={block.payload.primaryQuery} />;
}

/**
 * Collapsed "Review Gifs" disclosure. Expanding reveals the contextual category
 * pills + a search box; results stream from `GET /api/v2/gifs` in a two-column
 * layout. Presentational: picking a gif calls `onSelectGif` with a
 * {@link SocialAttachment}.
 */
export function GifPicker({
	pills,
	primaryQuery,
	onSelectGif,
	isReadOnly = false,
}: {
	pills: GifPickerPill[];
	primaryQuery?: string | null;
	onSelectGif?: (attachment: SocialAttachment) => void;
	isReadOnly?: boolean;
}) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const http = useHttpClient();
	const disclosure = useDisclosure();
	const containerRef = useRef<HTMLDivElement>(null);

	const [inputValue, setInputValue] = useState(primaryQuery ?? pills[0]?.query ?? '');
	const query = useDebounceValue(inputValue, 300);

	const canSelect = !isReadOnly && !!onSelectGif;

	const { data: { gifs: [left, right] = [[], []], provider = 'klipy' as GifProvider } = {}, isLoading } = useQuery<{
		gifs: [Gif[], Gif[]];
		provider: GifProvider;
	}>({
		queryKey: ['bizy-gifs', query],
		queryFn: async () => {
			const res = await http.get('/gifs', { params: { query } });
			const gifs = res.data.result.map((gif: { id: string }) => new Gif(gif.id, gif as never));
			return { gifs: split(gifs), provider: (res.data.provider || 'klipy') as GifProvider };
		},
		// Lazy: only fetch once the user opens the picker.
		enabled: disclosure.isOpen,
	});

	const providerConfig = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.klipy;

	const handleToggle = () => {
		const willOpen = !disclosure.isOpen;
		if (willOpen) {
			track(AnalyticsEvent.Bizy.GifPickerOpened, { path: window.location.pathname });
		}
		disclosure.onToggle();
		// On expand, scroll the now-taller picker into view so it isn't left
		// below the fold (the results area is a fixed height, so the body's
		// full height is known on the next frame).
		if (willOpen) {
			requestAnimationFrame(() => containerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }));
		}
	};

	const handlePill = (pillQuery: string) => {
		setInputValue(pillQuery);
		track(AnalyticsEvent.Bizy.GifPillClicked, { query: pillQuery, path: window.location.pathname });
	};

	const handleSelect = (gif: Gif) => {
		if (!canSelect) return;
		onSelectGif?.(gif.toAttachment());
		track(AnalyticsEvent.Bizy.GifSelected, { path: window.location.pathname });
	};

	return (
		<Box ref={containerRef} mt={2} border="1px solid" borderColor="border.default" borderRadius="md" bg="bg.surface" overflow="hidden">
			<Flex as="button" type="button" w="100%" align="center" justify="space-between" px={3} py={2} onClick={handleToggle} aria-expanded={disclosure.isOpen}>
				<Text fontSize="sm" fontWeight="semibold">
					{t('uiBlocks.gifPicker.title')}
				</Text>
				<ChevronUpOutline transitionProperty="transform" transitionDuration="fast" transform={disclosure.isOpen ? 'none' : 'rotate(180deg)'} />
			</Flex>

			{/* Plain conditional render (not Collapse): Collapse measures the body
			    height once on open — before the async gifs load — and pins it,
			    which collapsed the results to a single line. */}
			{disclosure.isOpen && (
				<Box px={3} pb={3} role="group">
					<Input
						size="sm"
						mb={2}
						type="text"
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						placeholder={t('uiBlocks.gifPicker.searchPlaceholder', { provider: providerConfig.name })}
					/>

					{pills.length > 0 && (
						<HStack spacing={2} mb={2} overflowX="auto" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
							{pills.map((pill, i) => (
								<Button key={`${pill.label}-${i}`} size="xs" colorScheme="secondary" variant="ghost" flexShrink={0} onClick={() => handlePill(pill.query)}>
									{pill.label}
								</Button>
							))}
						</HStack>
					)}

					{isLoading ? (
						<Center h={`${MAX_RESULTS_HEIGHT}px`}>
							<Spinner />
						</Center>
					) : (
						<Flex gap={1} align="start" h={`${MAX_RESULTS_HEIGHT}px`} overflowY="auto" role="listbox">
							<GifColumn gifs={left} canSelect={canSelect} onSelect={handleSelect} />
							<GifColumn gifs={right} canSelect={canSelect} onSelect={handleSelect} />
						</Flex>
					)}

					<Text fontSize="xs" color="text.softer" mt={2} textAlign="right">
						{t('uiBlocks.gifPicker.poweredBy')}{' '}
						<Link href={providerConfig.url} isExternal fontWeight="semibold">
							{providerConfig.name}
						</Link>
					</Text>
				</Box>
			)}
		</Box>
	);
}

function GifColumn({ gifs, canSelect, onSelect }: { gifs: Gif[]; canSelect: boolean; onSelect: (gif: Gif) => void }) {
	return (
		<VStack spacing={1} flex={1} minW={0}>
			{gifs.map((gif) => (
				<Box
					as="button"
					type="button"
					key={gif.gif}
					role="option"
					w="100%"
					borderRadius="md"
					overflow="hidden"
					cursor={canSelect ? 'pointer' : 'default'}
					onClick={() => onSelect(gif)}
				>
					<Image alt={gif.content_description} src={gif.preview} w="100%" />
				</Box>
			))}
		</VStack>
	);
}
